import {
    DataTypes,
    DataTypes as DT,
    DimensionAggregationStrategies,
    DynamicFilterOperations as DFO,
    FilterOperations as FO,
    SlidingTimeScales,
    TimeScales,
} from 'business/definitions';
import {
    convertFilterOperatorForNullValue,
    convertToQueryFilters,
    isFreeTextField,
    sentenceToExploreFilters,
} from 'helpers/filterHelper';
import { parseStringToFloat, parseStringToInt } from 'helpers/parser';
import { stringifyParams } from 'helpers/urlHelpers';
import i18n from 'i18n';
import _ from 'lodash';
import { isDynamicYearFilter } from 'modules/decision/components/SentenceFilter/FilterManagees/YearFilter/helpers/definitions';
import { FILTER_CONNECTOR } from 'modules/decision/constants';
import {
    getFilterLeaves,
    isFilterGroup,
    mapTree,
    serializeFilterTree,
} from 'modules/decision/pages/Explore/helpers/filterTree';
import {
    type FilterGroupT,
    type FilterNodeT,
} from 'modules/decision/types/explore';

export const stringifySentenceFilter = (
    filter: Partial<SentenceFilterT>,
): string => `${filter.field}-${filter.operation}-${filter.value}`;

export const checkNullValueWithInvalidOperation = (
    filterValue: SentenceFilterT['value'] | undefined,
    filterOperation: SentenceFilterT['operation'] | undefined,
) =>
    filterValue === null &&
    !(filterOperation === FO.is_not_null || filterOperation === FO.is_null);

function checkFilters(filters: Partial<SentenceFilterT>[]): {
    [key: string]: string;
} {
    const errors: { [key: string]: string } = {};
    filters.forEach((filter) => {
        const nullValueWithInvalidOperation =
            checkNullValueWithInvalidOperation(filter.value, filter.operation);

        const isValueUndefined = filter.value === undefined;
        const invalidFloatValue =
            filter.type === DataTypes.float &&
            filter.operation !== FO.is_null &&
            Number.isNaN(parseStringToFloat(filter.value));
        const invalidIntegerValue =
            filter.type === DataTypes.integer &&
            filter.operation !== FO.is_null &&
            Number.isNaN(parseStringToInt(filter.value, 10));
        const invalidYearValue =
            filter.type === DataTypes.year &&
            filter.operation !== FO.is_null &&
            Number.isNaN(parseStringToInt(filter.value, 10)) &&
            !(filter.value && isDynamicYearFilter(filter.value));

        if (
            nullValueWithInvalidOperation ||
            isValueUndefined ||
            invalidFloatValue ||
            invalidIntegerValue ||
            invalidYearValue
        ) {
            errors[stringifySentenceFilter(filter)] = i18n.t(
                'common.incorrectValue',
            );
        }
    });
    return errors;
}

function getFilterValue(f: QueryFilterT): string {
    if (f.value == null) return '';
    if (f.valueScale) {
        const encodedValue = f.value
            .split('|')
            .map(encodeURIComponent)
            .join('|');

        return `${f.valueScale}|${encodedValue}`;
    }
    return `${f.value}`;
}

/**
 * Serialize a query to a string
 */
// Escape only the grammar delimiters so a filter value cannot break parsing (`%` first).
const escapeLeafAtom = (atom: string): string =>
    atom
        .replaceAll('%', '%25')
        .replaceAll(/\(/g, '%28')
        .replaceAll(/\)/g, '%29')
        .replaceAll(/,/g, '%2C');
const unescapeLeafAtom = (atom: string): string =>
    atom
        .replaceAll(/%2C/g, ',')
        .replaceAll(/%29/g, ')')
        .replaceAll(/%28/g, '(')
        .replaceAll(/%25/g, '%');

// Readable URL grammar for the nested filter tree: `and(field|op|val,or(field|op|val,field|op|val))`.
const serializeFilterNodeToUrl = (node: FilterNodeT<QueryFilterT>): string =>
    isFilterGroup(node)
        ? `${node.connector}(${node.children.map(serializeFilterNodeToUrl).join(',')})`
        : escapeLeafAtom(
              `${node.field}|${node.operation}|${getFilterValue(node)}`,
          );

const splitTopLevel = (inner: string): string[] => {
    const parts: string[] = [];
    let depth = 0;
    let current = '';
    for (const char of inner) {
        if (char === '(') depth += 1;
        if (char === ')') depth -= 1;
        if (char === ',' && depth === 0) {
            parts.push(current);
            current = '';
        } else {
            current += char;
        }
    }
    if (current) parts.push(current);
    return parts;
};

export const serialize = {
    metrics: (metrics: QueryMetricT[]): string[] =>
        metrics
            .filter((m) => !!m.field)
            .map((m) => `${m.field}|${m.aggregation}`),
    filters: (filters: QueryFilterT[]): string[] =>
        filters
            .filter((filter) => filter.field)
            .filter(
                (filter) =>
                    filter.value ||
                    filter.valueScale ||
                    (filter.value == null &&
                        [FO.equal, FO.not_equal].includes(filter.operation)),
            )
            .map(
                (filter) =>
                    `${filter.field}|${filter.operation}|${getFilterValue(
                        filter,
                    )}`,
            ),
    dimensions: (dimensions: QueryDimensionT[]): string[] =>
        dimensions
            .filter((dim) => dim.field)
            .map((dim) => {
                if (dim.scale) {
                    return `${dim.field}|${dim.scale}`;
                }
                if (dim.aggregationStrategy) {
                    return `${dim.field}||${dim.aggregationStrategy}`;
                }
                return `${dim.field}`;
            }),
    query(query: ExploreQueryT): PlainQueryT {
        const base = {
            metrics: this.metrics(query.metrics),
            dimensions: this.dimensions(query.dimensions),
            dashboard_filters: query.dashboardFilters
                ? this.filters(query.dashboardFilters)
                : [],
        };
        return query.filters.children.length > 0
            ? {
                  ...base,
                  filters: [],
                  filter_tree: serializeFilterNodeToUrl(query.filters),
              }
            : { ...base, filters: [] };
    },
};

/**
 * Wrapper around decodeURIComponent that cannot fail
 */
const safeDecodeURIComponent = (str: string): string => {
    try {
        return decodeURIComponent(str);
    } catch {
        return str;
    }
};

/**
 * @param {string} fltr
 * converts
 * fltr: string
 * to
 * {field: string, operation:string, value: string}
 */
function deserializeFilter(fltr: string): QueryFilterT {
    let filterValue: string | null = fltr
        .split('|')
        .slice(2)
        // todo: check if we can remove this line
        .map(safeDecodeURIComponent)
        .join('|');
    if (filterValue === '') filterValue = null;

    // javascript's null is serialized as a "null" string in queryurls
    const plainFilter = {
        field: fltr.split('|')[0],
        operation: fltr.split('|')[1],
        value: filterValue,
    };

    // @ts-expect-error [TS migration] Was not detected by flow
    if (plainFilter.value === null) return plainFilter;

    const isDynamicFilter = _.values(DFO).includes(plainFilter.operation);
    const isExtract = plainFilter.operation === 'extract';

    const splittedValue = plainFilter.value.split('|');
    if (splittedValue.length > 0 && (isExtract || isDynamicFilter)) {
        // @ts-expect-error [TS migration] (previously $FlowFixMe), we add a key to an object
        plainFilter.valueScale = splittedValue[0];
        if (isDynamicFilter) plainFilter.value = splittedValue[1];
        else if (isExtract)
            plainFilter.value = splittedValue.slice(1).join('|');
    }
    // @ts-expect-error [TS migration] Was not detected by flow
    return plainFilter;
}

const parseFilterNodeFromUrl = (input: string): FilterNodeT<QueryFilterT> => {
    const groupMatch = input.match(/^(and|or)\((.*)\)$/s);
    if (!groupMatch) return deserializeFilter(unescapeLeafAtom(input));
    const [, connector, inner] = groupMatch;
    return {
        connector: connector as FilterConnectorT,
        children: splitTopLevel(inner).map(parseFilterNodeFromUrl),
    };
};

export const parseFilterTreeFromUrl = (
    input: string,
): FilterGroupT<QueryFilterT> => {
    const node = parseFilterNodeFromUrl(input);
    return isFilterGroup(node)
        ? node
        : { connector: FILTER_CONNECTOR.and, children: [node] };
};

const isTimeScale = (scale: string | undefined): scale is TimeScalesT => {
    if (!scale) return false;
    const timeScales = Object.values(TimeScales) as string[];
    const slidingTimeScales = Object.values(SlidingTimeScales) as string[];
    return timeScales.includes(scale) || slidingTimeScales.includes(scale);
};

const isDimensionAggregationStrategy = (
    aggregationStrategy: string | undefined,
): aggregationStrategy is DimensionAggregationStrategy => {
    if (!aggregationStrategy) return false;
    const dimensionAggregationStrategies = Object.values(
        DimensionAggregationStrategies,
    ) as string[];
    return dimensionAggregationStrategies.includes(aggregationStrategy);
};

type RawExploreQueryT = {
    metrics: QueryMetricT[];
    dimensions: QueryDimensionT[];
    filters: QueryFilterT[];
    filtersConnector: FilterConnectorT;
    filterTree?: FilterGroupT<QueryFilterT>;
    dashboardFilters?: QueryFilterT[];
};

// See tests for examples
const deserialize = {
    metrics: (metrics: string[]): QueryMetricT[] =>
        metrics.map((metric) => {
            const [field, aggregation] = metric.split('|');
            return { field, aggregation };
        }),
    dimensions: (dimensions: string[]): QueryDimensionT[] =>
        dimensions.map((dimension, index) => {
            const [field, scale, aggregationStrategy] = dimension.split('|');
            if (isTimeScale(scale)) {
                return {
                    field,
                    scale,
                    type: DataTypes.date,
                    displayIndex: index,
                    aggregationStrategy: null,
                };
            }
            return {
                field,
                type: null,
                displayIndex: index,
                isTree: isDimensionAggregationStrategy(aggregationStrategy),
                aggregationStrategy: isDimensionAggregationStrategy(
                    aggregationStrategy,
                )
                    ? aggregationStrategy
                    : null,
            };
        }),
    filters: (filters: string[]): QueryFilterT[] =>
        filters.map(deserializeFilter),
    query(query: PlainQueryT): RawExploreQueryT {
        const queryObj: RawExploreQueryT = {
            metrics: this.metrics(query.metrics),

            dimensions: this.dimensions(query.dimensions),

            filters: this.filters(query.filters),
            filtersConnector:
                query.filters_connector === FILTER_CONNECTOR.or
                    ? FILTER_CONNECTOR.or
                    : FILTER_CONNECTOR.and,
            ...(query.filter_tree
                ? { filterTree: parseFilterTreeFromUrl(query.filter_tree) }
                : {}),
        };

        if (typeof query.dashboard_filters === 'object') {
            queryObj.dashboardFilters = this.filters(query.dashboard_filters);
        }
        return queryObj;
    },
};

export const checkQuery = (query: SentenceQueryT) => {
    const errors: {
        aggregation: string[];
        metrics: string[];
        filters: { [key: number]: string };
    } = {
        aggregation: [],
        metrics: [],
        filters: {},
    };
    query.metrics.forEach((m) => {
        if (!m.field) errors.metrics.push(i18n.t('sentence.selectMetric'));
        if (!m.aggregation)
            errors.aggregation.push(i18n.t('sentence.selectAggregation'));
    });
    const filterErrors = checkFilters(getFilterLeaves(query.filters));
    if (Object.keys(filterErrors).length) errors.filters = filterErrors;
    return errors;
};

/**
 * Return the aggregation from metrics. Returns null on inconsistency.
 * This function is needed since (and only since) we use only one aggregation in Sentence.
 * TODO allow the use of several different aggregations for the metrics
 */
export function getUniqueAggregation(
    metrics: ReadonlyArray<QueryMetricT>,
): string {
    const aggregations = metrics.map((m) => m.aggregation);
    if (aggregations.length) {
        const res = aggregations[0];
        if (aggregations.every((a) => a === res)) return res;
    }
    return '';
}

export const isLegitString = (str?: string | null): boolean =>
    str != null && str !== '' && str !== 'undefined';

/**
 * Checks if the given metricField is available with the new aggregation.
 */
export const isMetricAvailableWithNewAggregation = (
    newAggregation: Aggregation,
    metricField: string,
    availableMetrics: AvailableMetricT[],
): boolean => {
    const metric = availableMetrics.find((m) => m.field === metricField);
    if (!metric?.aggregations) return false;
    return metric.aggregations.includes(newAggregation);
};

/*
 * Validate a query.
 * It should have at least one not empty metric with one not empty aggregation.
 * All metrics should also have a not empty aggregation.
 */
export function isValidQuery(
    query:
        | { metrics: Pick<QueryMetricT, 'field' | 'aggregation'>[] }
        | undefined,
): boolean {
    return (
        query !== undefined &&
        query.metrics.length > 0 &&
        query.metrics.every(
            (metric) =>
                isLegitString(metric.field) &&
                isLegitString(metric.aggregation),
        )
    );
}

function isNullOrUndefined(value: string) {
    return value === null || value === undefined;
}

/*
 * Helper function to treat undefined and null as equal in some comparisons.
 */
export function makeUndefinedComparisonWithNullTrue(
    value: string,
    otherValue: string,
) {
    // If one value is undefined and the other is null, consider them equal
    if (isNullOrUndefined(value) && isNullOrUndefined(otherValue)) {
        return true;
    }
    // For other cases, let lodash handle the comparison
    return undefined;
}

export function queryToSearch(query: ExploreQueryT): string {
    return stringifyParams(serialize.query(query));
}

export const customizer = <T>(
    objValue: T | T[],
    srcValue: T | T[],
): T[] | undefined => {
    if (_.isArray(objValue)) {
        return objValue.concat(srcValue);
    }
    return undefined;
};

function addDisplayIndexToMetrics(
    metrics: ExploreMetricT[],
): SentenceMetricT[] {
    return metrics.map((metric, index) => ({
        displayIndex: index,
        ...metric,
    }));
}

function addDisplayIndexToDimensions(
    dimensions: ExploreDimensionT[],
    indexOffset: number,
): SentenceDimensionT[] {
    return dimensions.map((dimension, index) => ({
        displayIndex: indexOffset + index,
        isFirst: index === 0,
        ...dimension,
        isTree: dimension.isTree || false,
    }));
}

const enrichMetricsWithAvailableDimensions = (
    queryDimensions: QueryDimensionT[],
    availableDimensions: AvailableDimensionT[],
): ExploreDimensionT[] =>
    queryDimensions.map((dimension) => {
        const availableDimension = availableDimensions.find(
            (d) => d.field === dimension.field,
        ) as AvailableDimensionT;
        return {
            ...dimension,
            ...availableDimension,
        };
    });

const enrichMetricsWithAvailableMetrics = (
    queryMetrics: QueryMetricT[],
    availableMetrics: AvailableMetricT[],
): ExploreMetricT[] =>
    queryMetrics.map((metric) => ({
        ...metric,
        ...(availableMetrics.find(
            (m) => m.field === metric.field,
        ) as AvailableMetricT),
        aggregation: metric.aggregation as Aggregation,
    }));

const enrichFiltersWithAvailableFilters = (
    queryFilters: QueryFilterT[],
    availableFilters: AvailableElementT[],
): ExploreFilterT[] =>
    queryFilters.map((filter) => {
        const { operation } = filter;
        const availableFilter = availableFilters.find(
            (f) => f.field === filter.field,
        ) as AvailableElementT;
        return {
            ...availableFilter,
            ...filter,
            operation,
        };
    });

export function searchToQuery(
    getArgs: string,
    available: {
        metrics: AvailableMetricT[];
        dimensions: AvailableDimensionT[];
        filters: AvailableElementT[];
    },
): ExploreQueryT {
    const params = new URLSearchParams(getArgs);
    const plainQuery: PlainQueryT = {
        metrics: params.getAll('metrics'),
        dimensions: params.getAll('dimensions'),
        filters: params.getAll('filters'),
        filters_connector: params.get('filters_connector') ?? undefined,
        filter_tree: params.get('filter_tree') ?? undefined,
    };

    const query = deserialize.query(plainQuery);

    // The nested tree wins when present; otherwise the flat filters become a single root group.
    const filters: FilterGroupT<ExploreFilterT> = query.filterTree
        ? mapTree(
              query.filterTree,
              (leaf) =>
                  enrichFiltersWithAvailableFilters(
                      [leaf],
                      available.filters,
                  )[0],
          )
        : {
              connector: query.filtersConnector,
              children: enrichFiltersWithAvailableFilters(
                  query.filters,
                  available.filters,
              ),
          };

    // TODO: Décoréler l'ajout du displayIndex et l'ajout des métadonnées
    return {
        metrics: enrichMetricsWithAvailableMetrics(
            query.metrics,
            available.metrics,
        ),
        dimensions: enrichMetricsWithAvailableDimensions(
            query.dimensions,
            available.dimensions,
        ),
        filters,
    };
}

/**
 * Convert a backend-compatible query to a frontend sentence.
 * We add metadata and add special values, such as is_null or between.
 */
export function queryToSentence(query: ExploreQueryT): SentenceQueryT {
    const sentenceMetrics = addDisplayIndexToMetrics(query.metrics);

    let indexOffset = sentenceMetrics.length;
    const sentenceDimensions = addDisplayIndexToDimensions(
        query.dimensions,
        indexOffset,
    );

    indexOffset += sentenceDimensions.length;
    let leafIndex = 0;
    const sentenceFilters = mapTree(query.filters, (filter) => {
        const enriched = {
            ...filter,
            displayIndex: indexOffset + leafIndex,
            isFirst: leafIndex === 0,
        };
        leafIndex += 1;
        return enriched;
    });
    return {
        metrics: sentenceMetrics,
        dimensions: sentenceDimensions,
        filters: sentenceFilters,
    };
}

function sentenceToQueryMetrics(
    sentenceMetrics: QueryMetricT[],
): QueryMetricT[] {
    return sentenceMetrics.map((sentenceMetric) => {
        const queryMetric: QueryMetricT = {
            field: sentenceMetric.field,
            aggregation: sentenceMetric.aggregation,
        };
        return queryMetric;
    });
}

function sentenceToQueryDimensions(
    sentenceDimensions: QueryDimensionT[],
): QueryDimensionT[] {
    return sentenceDimensions.map((sentenceDimension) => {
        const queryDimension: QueryDimensionT = {
            field: sentenceDimension.field,
            displayIndex: sentenceDimension.displayIndex,
            scale: sentenceDimension.scale || undefined,
            aggregationStrategy: sentenceDimension.aggregationStrategy || null,
        };
        return queryDimension;
    });
}

const removeEmptyFields = (e: { field: QueryMetricT['field'] }) =>
    e.field !== '';
const addStaticDisplayIndexDimension = (
    e: QueryDimensionT,
): QueryDimensionT => ({
    field: e.field,
    displayIndex: 0,
    scale: e.scale,
    aggregationStrategy: e.aggregationStrategy,
});

// Order-independent key for a node, so reordering siblings is not seen as a change while regrouping is.
const filterNodeSortKey = (node: FilterNodeT<QueryFilterT>): string =>
    isFilterGroup(node)
        ? `${node.connector}(${node.children.map(filterNodeSortKey).sort().join(',')})`
        : `${node.field}|${node.operation}|${node.value ?? ''}`;

/**
 * Normalize a filter tree by removing empty leaves and sorting children.
 * If a group has only one child, it is replaced by that child and the connector is set to AND.
 * This ensures that the same filter tree structure is always represented in the same way.
 */
const normalizeFilterTree = (
    tree: FilterGroupT<QueryFilterT>,
): FilterGroupT<QueryFilterT> => {
    const children = tree.children
        .flatMap((child): FilterNodeT<QueryFilterT>[] => {
            if (!isFilterGroup(child)) {
                return child.field ? convertToQueryFilters([child]) : [];
            }
            const normalized = normalizeFilterTree(child);
            return normalized.children.length > 0 ? [normalized] : [];
        })
        .sort((a, b) =>
            filterNodeSortKey(a).localeCompare(filterNodeSortKey(b)),
        );
    return {
        connector: children.length > 1 ? tree.connector : FILTER_CONNECTOR.and,
        children,
    };
};

/**
 * Simplify a query for comparison by removing empty fields and normalizing the filter tree.
 */
export function simplifySentenceQueryForComparison(
    query: SentenceQueryT | ExploreQueryT,
): {
    metrics: QueryMetricT[];
    dimensions: QueryDimensionT[];
    filters: FilterGroupT<QueryFilterT>;
} {
    const tree: FilterGroupT<QueryFilterT> = query.filters;
    return {
        metrics: _.sortBy(
            sentenceToQueryMetrics(query.metrics.filter(removeEmptyFields)),
            (metric) => metric.field,
        ),
        dimensions: _.sortBy(
            sentenceToQueryDimensions(
                query.dimensions.filter(removeEmptyFields),
            ).map(addStaticDisplayIndexDimension),
            (dimension) => dimension.field,
        ),
        filters: normalizeFilterTree(tree),
    };
}

/**
 * Convert a sentence to an explore query that is compatible with the backend
 */
export function sentenceToExplore(query: SentenceQueryT): ExploreQueryT {
    const queryFilters = mapTree(query.filters, (f) => ({
        ...convertFilterOperatorForNullValue(f),
        type: f.type,
    }));

    const queryMetrics = query.metrics.map((m) => ({
        field: m.field,
        aggregation: m.aggregation,
        type: m.type,
    }));
    const queryDimensions: ExploreDimensionT[] = query.dimensions.map((d) => ({
        field: d.field,
        scale: d.scale,
        type: d.type,
        isTree: d.isTree,
        aggregationStrategy: d.aggregationStrategy,
        // Do NOT remove the below index, it is useful to order dimensions !
        displayIndex: d.displayIndex,
    }));
    return {
        metrics: queryMetrics,
        dimensions: queryDimensions,
        filters: queryFilters,
    };
}

/**
 * Convert a explore state to a query that is compatible with the backend
 */
export function exploreToQuery(query: ExploreQueryT): QueryT {
    const queryMetrics = query.metrics.map((m) => ({
        field: m.field,
        aggregation: m.aggregation,
    }));
    const queryDimensions: QueryDimensionT[] = query.dimensions.map((d) => ({
        field: d.field,
        scale: d.scale,
        aggregationStrategy: d.aggregationStrategy,
        // Do NOT remove the below index, it is useful to order dimensions !
        displayIndex: d.displayIndex,
    }));
    return {
        metrics: queryMetrics,
        dimensions: queryDimensions,
        filterExpression: serializeFilterTree(query.filters),
    };
}

/**
 * Merging perimeter filters to the given filters.
 */
export function mergeDashboardFilters(
    dashboardFilters: DashboardFilterT[],
    filters: QueryFilterT[],
): QueryFilterT[] {
    const perimeterFilters = dashboardFilters.filter(
        (f) => f.value != null && f.value !== '',
    );
    const queryFilters = filters.filter(
        (f) =>
            !perimeterFilters
                .map((perimeterFilter) => perimeterFilter.field)
                .includes(f.field),
    );
    return [...queryFilters, ...convertToQueryFilters(perimeterFilters)];
}

/**
 * Merging perimeter filters to the given explore filters.
 */
export function mergeDashboardFiltersWithExplore(
    dashboardFilters: DashboardFilterT[],
    filters: ExploreFilterT[],
): ExploreFilterT[] {
    const dashboardFiltersWithValues = dashboardFilters.filter((f) =>
        Boolean(f.value),
    );
    const queryFilters = filters.filter(
        (f) =>
            !dashboardFiltersWithValues
                .map((dashboardFilter) => dashboardFilter.field)
                .includes(f.field),
    );
    return [
        ...queryFilters,
        ...sentenceToExploreFilters(dashboardFiltersWithValues),
    ];
}

/**
 * This function gets the value that should be in the new filter
 * If the field changed, this value will always be an empty string
 * except in these situations, where the old value is kept:
 *      - the old and new fields are of the same type and not a category type
 *      - the old and new operations require a free text input value
 * @returns the value of the new filter
 */
export const getUpdatedFilterValue = (
    oldFilter: Partial<SentenceFilterT>,
    newFilter: SentenceFilterT,
): string => {
    const oldType = oldFilter.type;
    const newType = newFilter.type;
    if (oldFilter.field !== newFilter.field) {
        const areFreeTextFields =
            // TODO StrictNullCheck : Try to remove "!"
            isFreeTextField(oldFilter.operation!) &&
            isFreeTextField(newFilter.operation);
        if (
            areFreeTextFields ||
            (newType !== DT.category && oldType === newType)
        ) {
            // TODO StrictNullCheck : Try to remove "!"
            return oldFilter.value!;
        }
        return '';
    }
    // TODO StrictNullCheck : Try to remove "!"
    return newFilter.value!;
};
